import BusinessIcon from '@mui/icons-material/Business'
import {
  Container,
  Typography,
  Box,
  LinearProgress,
  Avatar
} from '@mui/material'
import React from 'react'
import { useNavigate, Navigate } from 'react-router-dom'
import { useRecoilValue } from 'recoil'

import { AppBarR as PersistentDrawerRight, Wrapper } from '../components/common'
import { OfficeForm, OfficeCreationSuccess } from '../components/office'
import { useOfficeForm } from '../hooks/useOfficeForm'
import { sessionState } from '../state'

import routes from './routes'

/**
 * CreateOfficePage Component
 *
 * Main page component for office creation functionality.
 * Integrates useOfficeForm hook with presentation components,
 * manages creation vs success state transitions, and handles navigation.
 *
 * Requirements: 1.1, 1.5, 4.2, 4.3, 4.4
 */
const CreateOfficePage: React.FC = () => {
  const navigate = useNavigate()
  const session = useRecoilValue(sessionState)

  // Initialize the office form hook (must be called before any early returns)
  const {
    formData,
    isSubmitting,
    isRetrying,
    retryCount,
    validationErrors,
    successMessage,
    createdOffice,
    updateField,
    submitForm,
    clearMessage
  } = useOfficeForm({
    user: session.user,
    office: null, // Creating new office
    onCreate: office => {
      // Office created successfully - navigate directly to office details screen
      navigate(`/office/${office.oid}`)
    }
  })

  // Redirect unauthenticated users to sign-in page
  if (!session.isAuthenticating && !session.user) {
    return <Navigate to={routes.signin} replace />
  }

  // Navigation is now handled internally by OfficeCreationSuccess component

  // Determine if we should show success state
  const showSuccessState = createdOffice !== null && successMessage

  return (
    <>
      <PersistentDrawerRight />
      <Wrapper>
        <Container maxWidth='sm'>
          <Box sx={{ mt: 4, mb: 4 }}>
            {/* Loading indicator for page-level operations */}
            {(isSubmitting || isRetrying) && (
              <Box sx={{ mb: 2 }}>
                <LinearProgress
                  sx={{
                    borderRadius: 1,
                    height: 4,
                    backgroundColor: 'rgba(0,0,0,0.1)',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: isRetrying ? '#ff9800' : '#3b82f6'
                    }
                  }}
                />
              </Box>
            )}

            {!showSuccessState ? (
              <>
                {/* Office Creation Form */}
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}
                >
                  <Avatar
                    sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}
                  >
                    <BusinessIcon fontSize='small' />
                  </Avatar>
                  <Typography
                    component='h1'
                    variant='h6'
                    sx={{ fontWeight: 600 }}
                  >
                    Create New Office
                  </Typography>
                </Box>

                <Typography
                  variant='body1'
                  color='text.secondary'
                  sx={{ mb: 3 }}
                  data-testid='create-office-subtitle'
                >
                  Set up a new office space where your team can book desks and
                  collaborate.
                </Typography>

                <OfficeForm
                  formData={formData}
                  isSubmitting={isSubmitting}
                  isRetrying={isRetrying}
                  retryCount={retryCount}
                  validationErrors={validationErrors}
                  successMessage={successMessage}
                  onSubmit={submitForm}
                  onChange={updateField}
                  onClearMessage={clearMessage}
                />
              </>
            ) : (
              <>
                {/* Office Creation Success */}
                <OfficeCreationSuccess office={createdOffice} />
              </>
            )}
          </Box>
        </Container>
      </Wrapper>
    </>
  )
}

export default CreateOfficePage
